import type { Uuid } from '@common/types';
import type { FormWidgetRulesData, PaymentFormWidgetRulesData } from '@sites/entity-core';
import type { WidgetsErrorsState } from './widgets-errors.reducer';
import type { PageStructureState } from '../page-structure-feature.reducer';

import { createSelector } from '@ngrx/store';

import { getFormWidgetRuleIssues, getPaymentFormWidgetRuleIssues } from '@sites/entity-core';
import { Widgets } from '@common/enums';
import { WIDGETS_ERRORS_FEATURE_KEY } from './widgets-errors.reducer';
import { pageStructureState } from '@web/store/page-structure/page-structure-state.selectors';
import { selectAllWidgets } from '@web/store/page-structure/widgets/widgets.selectors';

export const selectWidgetsErrorsState = createSelector(
    pageStructureState,
    (state: PageStructureState): WidgetsErrorsState => state[WIDGETS_ERRORS_FEATURE_KEY]
);

/** The publish checks of the widgets validated by their signal form, run on plain widget data. */
const WIDGET_RULES: Partial<Record<Widgets, (data: unknown) => { message: string }[]>> = {
    [Widgets.FORM]: (data) => getFormWidgetRuleIssues(data as FormWidgetRulesData),
    [Widgets.PAYMENT_FORM]: (data) => getPaymentFormWidgetRuleIssues(data as PaymentFormWidgetRulesData),
};

export type WidgetRuleErrors = { name: Widgets; messages: string[] };

/**
 * Failed publish checks (translation keys) of the widgets that validate in their signal form, derived
 * from the widget data with the same rules — nothing is stored for them.
 */
export const selectWidgetsRuleErrors = createSelector(selectAllWidgets, (widgets) =>
    widgets.reduce<Record<Uuid, WidgetRuleErrors>>((result, widget) => {
        const rules = WIDGET_RULES[widget.name as Widgets];
        if (!rules || !widget.data) {
            return result;
        }

        const messages = rules(widget.data).map(({ message }) => message);
        if (messages.length) {
            result[widget.id] = { name: widget.name as Widgets, messages };
        }

        return result;
    }, {})
);
